/// <reference path="./theme.d.ts" />

import { ChipProps } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { SYSTEM_FONT_STACK } from './myfonts';

export type AvatarSize = 'large' | 'medium' | 'small' | 'xlarge';
export type ProfileScoreCircularSize = 'large' | 'medium' | 'small';

export const SHADCN_COLORS = {
  accent: '#f4f4f5',
  accentForeground: '#18181b',
  background: '#ffffff',
  border: '#e4e4e7',
  card: '#ffffff',
  cardForeground: '#09090b',
  destructive: '#ef4444',
  destructiveHover: '#dc2626',
  foreground: '#09090b',
  input: '#e4e4e7',
  muted: '#f4f4f5',
  mutedForeground: '#71717a',
  popover: '#ffffff',
  popoverForeground: '#09090b',
  primary: '#18181b',
  primaryForeground: '#fafafa',
  primaryHover: '#27272a',
  ring: '#a1a1aa',
  rowHover: '#fafafa',
  secondary: '#a1a1aa',
  secondaryForeground: '#18181b',
  white: '#ffffff',
};

const STATUS_COLORS = {
  chipSuccess: '#22c55e',
  chipWarning: '#f59e0b',
  deleted: '#D32F2F',
  pending: '#FFA726',
  rejected: '#ED6C02',
  verified: '#4FC555',
};

const BRAND_COLORS = {
  igluIce: '#8EE8FF',
  igluJeans: '#14B5DD',
  igluPacific: '#3A5799',
  igluSapphire: '#4285F4',
  igluSky: '#00C5F6',
  igluTwilight: '#021033',
};

const RADIUS = {
  full: '9999px',
  lg: '10px',
  md: '8px',
  sm: '6px',
  xl: '14px',
};

const SHADOWS = {
  focusRing: `0 0 0 3px ${SHADCN_COLORS.ring}26`,
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  xl: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
};

const FONT_SIZE = {
  '2xl': 16,
  '3xl': 24,
  base: '0.875rem',
  lg: 12,
  sm: '0.8125rem',
  xl: 14,
  xs: '0.75rem',
};

const FONT_WEIGHT = {
  medium: '500',
  regular: '400',
  semibold: 600,
};

const LINE_HEIGHT = {
  normal: '1.5rem',
  relaxed: '2rem',
  tight: '1.25rem',
};

const LETTER_SPACING = {
  normal: 0,
  snug: '-0.01em',
  tight: '-0.025em',
};

const MENU_ITEM_MARGIN = '2px 4px';

const TAB_INDICATOR_HEIGHT = 2;

let theme = createTheme({
  palette: {
    background: {
      default: SHADCN_COLORS.background,
      paper: SHADCN_COLORS.card,
    },
    chip: {
      main: SHADCN_COLORS.secondary,
    },
    chip_grey: {
      contrastText: SHADCN_COLORS.foreground,
      main: SHADCN_COLORS.muted,
    },
    deleted: {
      contrastText: SHADCN_COLORS.white,
      main: STATUS_COLORS.deleted,
    },
    divider: SHADCN_COLORS.border,
    error: {
      main: SHADCN_COLORS.destructive,
    },
    iglu_ice: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluIce,
    },
    iglu_jeans: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluJeans,
    },
    iglu_pacific: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluPacific,
    },
    iglu_sapphire: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluSapphire,
    },
    iglu_sky: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluSky,
    },
    iglu_twilight: {
      contrastText: SHADCN_COLORS.white,
      main: BRAND_COLORS.igluTwilight,
    },
    pending: {
      contrastText: SHADCN_COLORS.white,
      main: STATUS_COLORS.pending,
    },
    primary: {
      contrastText: SHADCN_COLORS.primaryForeground,
      dark: SHADCN_COLORS.primaryHover,
      main: SHADCN_COLORS.primary,
    },
    rejected: {
      contrastText: SHADCN_COLORS.white,
      main: STATUS_COLORS.rejected,
    },
    secondary: {
      dark: SHADCN_COLORS.border,
      main: SHADCN_COLORS.secondary,
    },
    text: {
      disabled: SHADCN_COLORS.mutedForeground,
      primary: SHADCN_COLORS.foreground,
      secondary: SHADCN_COLORS.mutedForeground,
    },
    verified: {
      contrastText: SHADCN_COLORS.white,
      main: STATUS_COLORS.verified,
    },
  },
  shape: {
    borderRadius: 10,
  },
  topNavigation: {
    active: SHADCN_COLORS.foreground,
    main: SHADCN_COLORS.mutedForeground,
  },
  typography: {
    allVariants: {
      color: SHADCN_COLORS.foreground,
    },
    bodyNumbers: {},
    fontFamily: SYSTEM_FONT_STACK,
    fontSize: FONT_SIZE.xl,
    fontWeightMedium: FONT_WEIGHT.medium,
    fontWeightRegular: FONT_WEIGHT.regular,
  },
});

theme = createTheme(theme, {
  avatarSizes: {
    large: {
      height: theme.spacing(9),
      width: theme.spacing(9),
    },
    medium: {
      height: theme.spacing(5),
      width: theme.spacing(5),
    },
    small: {
      height: theme.spacing(3.5),
      width: theme.spacing(3.5),
    },
    xlarge: {
      height: theme.spacing(19),
      width: theme.spacing(19),
    },
  },
  cardStyles: {
    border: `1px solid ${SHADCN_COLORS.border}`,
    borderRadius: RADIUS.lg,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: SHADCN_COLORS.background,
          borderBottom: `1px solid ${SHADCN_COLORS.border}`,
          boxShadow: SHADOWS.none,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          fontSize: FONT_SIZE.base,
        },
        paper: {
          borderRadius: RADIUS.md,
          boxShadow: SHADOWS.md,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {},
    },
    MuiBox: {
      styleOverrides: {
        root: {
          minHeight: 20,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        containedError: {
          '&:hover': {
            backgroundColor: SHADCN_COLORS.destructiveHover,
          },
          backgroundColor: SHADCN_COLORS.destructive,
          color: SHADCN_COLORS.white,
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: SHADCN_COLORS.primaryHover,
          },
          backgroundColor: SHADCN_COLORS.primary,
          color: SHADCN_COLORS.primaryForeground,
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: SHADCN_COLORS.accent,
            borderColor: SHADCN_COLORS.border,
          },
          borderColor: SHADCN_COLORS.input,
          color: SHADCN_COLORS.accentForeground,
        },
        root: {
          borderRadius: RADIUS.md,
          boxShadow: SHADOWS.none,
          fontWeight: FONT_WEIGHT.medium,
          letterSpacing: LETTER_SPACING.normal,
          textTransform: 'none',
        },
        textPrimary: {
          '&:hover': {
            backgroundColor: SHADCN_COLORS.accent,
          },
          color: SHADCN_COLORS.accentForeground,
          textTransform: 'none',
        },
        textSecondary: {
          '&:hover': {
            backgroundColor: SHADCN_COLORS.accent,
            borderRadius: RADIUS.md,
          },
          color: SHADCN_COLORS.mutedForeground,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: SHADCN_COLORS.border,
          borderRadius: RADIUS.lg,
          minHeight: 20,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(3),
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSuccess: {
          backgroundColor: STATUS_COLORS.chipSuccess,
        },
        colorWarning: {
          backgroundColor: STATUS_COLORS.chipWarning,
        },
        label: {
          color: SHADCN_COLORS.secondaryForeground,
          fontSize: FONT_SIZE.xs,
          fontWeight: FONT_WEIGHT.medium,
        },
        root: ({ ownerState }: { ownerState: ChipProps }) => ({
          borderRadius: RADIUS.md,
          ...(ownerState.variant === 'skills' && {
            '& .MuiChip-label': { color: SHADCN_COLORS.foreground },
          }),
        }),
      },
      variants: [
        {
          props: { variant: 'skills' },
          style: {
            backgroundColor: SHADCN_COLORS.muted,
            borderRadius: RADIUS.sm,
          },
        },
      ],
    },
    MuiDataGrid: {
      styleOverrides: {
        '.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, .MuiDataGrid-root .MuiDataGrid-cell:focus':
          {
            outline: `solid ${SHADCN_COLORS.border} 1px`,
          },
        cell: {
          '& :focus': {
            outline: 'red',
          },
          alignItems: 'center',
          borderColor: SHADCN_COLORS.border,
          display: 'flex',
          fontSize: FONT_SIZE.base,

          padding: theme.spacing(2),
        },
        columnHeader: {
          color: SHADCN_COLORS.mutedForeground,
          fontSize: FONT_SIZE.sm,
          fontWeight: FONT_WEIGHT.medium,
          padding: theme.spacing(0, 2),
        },
        columnHeaderRow: {
          '&&&&': { background: 'none' },
        },
        columnSeparator: {
          display: 'none',
        },
        root: {
          border: 'none',
          fontFamily: SYSTEM_FONT_STACK,
          fontSize: FONT_SIZE.base,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS.xl,
          boxShadow: SHADOWS.xl,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontFamily: SYSTEM_FONT_STACK,
          fontSize: FONT_SIZE.xl,
          fontWeight: FONT_WEIGHT.regular,
          lineHeight: LINE_HEIGHT.normal,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: SHADCN_COLORS.border,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontFamily: SYSTEM_FONT_STACK,
          fontSize: FONT_SIZE.xl,
          fontStyle: 'normal',
          fontWeight: FONT_WEIGHT.regular,
          lineHeight: LINE_HEIGHT.normal,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZE.xs,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: SHADCN_COLORS.foreground,
          fontSize: FONT_SIZE.base,
          fontWeight: FONT_WEIGHT.medium,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.md,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZE.base,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: SHADCN_COLORS.foreground,
          fontSize: FONT_SIZE.base,
          fontWeight: FONT_WEIGHT.medium,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: SYSTEM_FONT_STACK,
          fontSize: FONT_SIZE.xl,
          fontWeight: FONT_WEIGHT.regular,
          lineHeight: LINE_HEIGHT.normal,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS.md,
          boxShadow: SHADOWS.md,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.sm,
          fontSize: FONT_SIZE.base,
          margin: MENU_ITEM_MARGIN,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: SHADCN_COLORS.input,
          transition: 'border-color 0.15s, box-shadow 0.15s',
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: SHADCN_COLORS.ring,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: SHADCN_COLORS.ring,
            borderWidth: '1px',
            boxShadow: SHADOWS.focusRing,
          },
          borderRadius: RADIUS.md,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: SHADCN_COLORS.border,
        },
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS.xl,
          boxShadow: SHADOWS.md,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: SHADCN_COLORS.primary,
          },
          color: SHADCN_COLORS.primary,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.md,
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          minHeight: 20,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: SHADCN_COLORS.primaryForeground,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: SHADCN_COLORS.primary,
            opacity: 1,
          },
        },
        track: {
          backgroundColor: SHADCN_COLORS.input,
          borderRadius: RADIUS.full,
          opacity: 1,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: SHADCN_COLORS.foreground,
          },
          color: SHADCN_COLORS.mutedForeground,
          fontWeight: FONT_WEIGHT.medium,
          letterSpacing: LETTER_SPACING.normal,
          textTransform: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: SHADCN_COLORS.mutedForeground,
          fontSize: FONT_SIZE.sm,
          fontWeight: FONT_WEIGHT.medium,
        },
        root: {
          borderColor: SHADCN_COLORS.border,
          fontSize: FONT_SIZE.base,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: FONT_SIZE.base,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: SHADCN_COLORS.foreground,
          height: TAB_INDICATOR_HEIGHT,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: SHADCN_COLORS.primaryHover,
            },
            backgroundColor: SHADCN_COLORS.primary,
            color: SHADCN_COLORS.primaryForeground,
          },
          borderColor: SHADCN_COLORS.input,
          borderRadius: RADIUS.md,
          fontWeight: FONT_WEIGHT.medium,
          textTransform: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SHADCN_COLORS.foreground,
          borderRadius: RADIUS.sm,
          color: SHADCN_COLORS.background,
          fontSize: FONT_SIZE.xs,
          fontWeight: FONT_WEIGHT.medium,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          mHeading: 'p',
        },
      },
    },
  },
  cssVariables: true,
  modalStyles: {
    borderRadius: RADIUS.xl,
  },
  profileScoreCircularSizes: {
    large: {
      height: '186px',
      width: '186px',
    },
    medium: {
      height: '100px',
      width: '100px',
    },
    small: {
      height: '50px',
      width: '50px',
    },
  },
  typography: {
    body: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE.xl,
      fontWeight: FONT_WEIGHT.regular,
      lineHeight: LINE_HEIGHT.normal,
    },
    bodyBold: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE.xl,
      fontWeight: FONT_WEIGHT.medium,
      lineHeight: LINE_HEIGHT.normal,
    },
    bodySmall: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE.lg,
      fontWeight: FONT_WEIGHT.regular,
      lineHeight: LINE_HEIGHT.tight,
    },
    bodySmallBold: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE.lg,
      fontWeight: FONT_WEIGHT.medium,
      lineHeight: LINE_HEIGHT.tight,
    },
    headingLarge: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE['3xl'],
      fontWeight: FONT_WEIGHT.semibold,
      letterSpacing: LETTER_SPACING.tight,
      lineHeight: LINE_HEIGHT.relaxed,
    },
    headingMedium: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE['2xl'],
      fontWeight: FONT_WEIGHT.semibold,
      letterSpacing: LETTER_SPACING.snug,
      lineHeight: LINE_HEIGHT.normal,
    },
    headingSmall: {
      fontFamily: SYSTEM_FONT_STACK,
      fontSize: FONT_SIZE.xl,
      fontWeight: FONT_WEIGHT.medium,
      lineHeight: LINE_HEIGHT.tight,
    },
  },
});

export { theme };
